import {
	Button,
	createMuiTheme,
	MuiThemeProvider,
	TextField,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	deleteArtisticMovement,
	getArtisticMovementById,
	postArtisticMovement,
	updateArtisticMovement,
} from "../../../../api/artisticMovement";
import { postLog } from "../../../../api/log";
import { LOGS } from "../../../../utils/constants";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyArtisticMovement() {
	return {
		name: "",
	};
}

export default function EditArtisticMovementsPage() {
	const [artisticMovement, setArtisticMovement] = useState(
		getEmptyArtisticMovement()
	);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

	const artisticMovementId = useParams().id;
	const history = useHistory();
	const location = useLocation();

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!artisticMovementId) {
			disableLoadingData();
			return;
		}
		getArtisticMovementById(artisticMovementId)
			.then((res) => {
				if (res.status === 200) {
					setArtisticMovement(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get artistic movement.",
				});
				handleBack();
			});
	}, [artisticMovementId, disableLoadingData, history]);

	const handleBack = () => {
		const prevTablePage = location?.state?.page;

		history.push(
			`/artistic-movements${
				prevTablePage ? "?page=" + prevTablePage : ""
			}`
		);
	};

	function saveArtisticMovement() {
		if (!artisticMovementId) {
			postArtisticMovement(artisticMovement)
				.then((res) => {
					if (res.status === 201) {
						postLog(
							loggedUser,
							LOGS.FIELDS.ARTISTIC_MOVEMENTS,
							`${LOGS.ACTIONS.CREATE}-${artisticMovement?.name}`
						);
						alertSuccess({
							title: "Saved!",
							customMessage:
								"Artistic movement successfully created.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save artistic movement.",
					});
				});
		} else {
			updateArtisticMovement(artisticMovementId, artisticMovement)
				.then((res) => {
					if (res.status === 200) {
						postLog(
							loggedUser,
							LOGS.FIELDS.ARTISTIC_MOVEMENTS,
							`${LOGS.ACTIONS.UPDATE}-${artisticMovement?.name}`
						);
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setArtisticMovement({
			...artisticMovement,
			[element]: event.target.value,
		});
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Edit artistic movement"></CardHeader>
					<CardBody>
						<TextField
							id={`fullName`}
							label="Name"
							value={artisticMovement.name}
							onChange={handleChange("name")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>
					</CardBody>
				</Card>
				<Button
					onClick={handleBack}
					variant="outlined"
					style={{ marginRight: "20px" }}
				>
					Back
				</Button>
				<Button
					onClick={() => saveArtisticMovement()}
					variant="outlined"
					color="primary"
					style={artisticMovementId && { marginRight: "20px" }}
				>
					Save artistic movement
				</Button>
				{artisticMovementId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								className="py-2"
								onClick={() => setOpenConfirmDialog(true)}
								variant="outlined"
								color="secondary"
							>
								Delete artistic movement
							</Button>
						</MuiThemeProvider>
						<ConfirmDialog
							title={
								"Are you sure you want to delete this artistic movement?"
							}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteArtisticMovement(artisticMovementId)
									.then((res) => {
										if (
											res.status === 204 ||
											res.status === 200
										) {
											postLog(
												loggedUser,
												LOGS.FIELDS.ARTISTIC_MOVEMENTS,
												`${LOGS.ACTIONS.DELETE}-${artisticMovement?.name}`
											);
											alertSuccess({
												title: "Deleted!",
												customMessage:
													"Artistic movement deleted successfully.",
											});
											handleBack();
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												"Could not delete artistic movement.",
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
